import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, Check, Plus } from 'lucide-react'
import { MainNav } from './MainNav'
import { UserMenu } from './UserMenu'
import { useAuth } from '@/context/AuthContext'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import type { NavigationItem } from './MainNav'
import { WelcomeModal } from '@/components/WelcomeModal'
import socratesBust from '@/assets/socrates-philosopher-bust.png'

const STATIC_NAV_ITEMS: NavigationItem[] = [
  { label: 'Matchday', shortLabel: 'Matchday', href: '/dashboard', icon: 'calendar' },
  { label: 'My Picks', shortLabel: 'Picks', href: '/picks', icon: 'clipboard' },
  { label: 'Standings', shortLabel: 'Standings', href: '/standings', icon: 'trophy' },
]

const AFTER_COMPETITION_NAV_ITEMS: NavigationItem[] = [
  { label: 'Fixtures', shortLabel: 'Fixtures', href: '/fixtures', icon: 'calendar-days' },
  { label: 'Billing', shortLabel: 'Billing', href: '/billing', icon: 'credit-card' },
]

interface LeaguePill {
  id: string
  name: string
}

function LeagueSwitcher({
  leagues,
  currentLeagueId,
  onLeagueChange,
  onCreateLeague,
}: {
  leagues: LeaguePill[]
  currentLeagueId?: string
  onLeagueChange?: (leagueId: string) => void
  onCreateLeague?: () => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const currentLeague = leagues.find(l => l.id === currentLeagueId) || leagues[0]
  if (!currentLeague) return null

  const hasMultiple = leagues.length > 1

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => hasMultiple && setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors
          bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300
          ${hasMultiple ? 'hover:bg-zinc-200 dark:hover:bg-zinc-700 cursor-pointer' : 'cursor-default'}`}
      >
        <span className="text-sm">🏆</span>
        <span className="truncate max-w-[140px]">{currentLeague.name}</span>
        {hasMultiple && (
          <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && hasMultiple && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 py-1 z-50">
          <p className="px-4 py-2 text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Switch League
          </p>
          {leagues.map((league) => (
            <button
              key={league.id}
              onClick={() => {
                onLeagueChange?.(league.id)
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <span className="text-base">🏆</span>
              <span className={`flex-1 ${currentLeagueId === league.id ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-zinc-700 dark:text-zinc-300'}`}>
                {league.name}
              </span>
              {currentLeagueId === league.id && (
                <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              )}
            </button>
          ))}
          <div className="border-t border-zinc-200 dark:border-zinc-700 mt-1 pt-1">
            <button
              onClick={() => {
                onCreateLeague?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <Plus className="w-4 h-4" />
              <span>Create a League</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, memberships, currentMembership, setCurrentMembership, signOut, isSuperAdmin } = useAuth()
  const { competition } = useLeagueConfig()

  // Build navigation items with competition-specific label and logo
  const competitionNavItem: NavigationItem = {
    label: competition.code,
    shortLabel: competition.code,
    href: '/league',
    icon: 'shield',
    ...(competition.logo && { iconUrl: competition.logo }),
  }

  const navItems = [...STATIC_NAV_ITEMS, competitionNavItem, ...AFTER_COMPETITION_NAV_ITEMS]

  const navigationItems: NavigationItem[] = navItems.map(item => ({
    ...item,
    isActive: location.pathname === item.href,
  }))

  // Build leagues list from memberships
  const leagues: LeaguePill[] = memberships.map(m => ({
    id: m.league_id,
    name: m.league.name,
  }))

  const currentLeagueId = currentMembership?.league_id

  const handleNavigate = (href: string) => {
    navigate(href)
  }

  const handleLeagueChange = (leagueId: string) => {
    const membership = memberships.find(m => m.league_id === leagueId)
    if (membership) {
      setCurrentMembership(membership)
    }
  }

  // Build user display info
  const userName = currentMembership?.display_name
    || user?.user_metadata?.name
    || user?.email?.split('@')[0]
    || 'User'
  const userEmail = user?.email || ''
  const initials = userName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 pt-[env(safe-area-inset-top)]">
        <div className="flex items-center justify-between h-14 px-4
                        md:h-16 md:px-6
                        lg:max-w-6xl lg:mx-auto">
          {/* Logo — links to About / white paper */}
          <button
            onClick={() => navigate('/about', { state: { from: location.pathname } })}
            className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center overflow-hidden">
              <img src={socratesBust} alt="Socrates" className="w-full h-full object-cover" />
            </div>
            <span className="font-caveat text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              Socrates
            </span>
          </button>

          {/* League Indicator — centered */}
          {leagues.length > 0 && (
            <div className="absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0 md:flex-1 md:flex md:justify-center">
              <LeagueSwitcher
                leagues={leagues}
                currentLeagueId={currentLeagueId}
                onLeagueChange={handleLeagueChange}
                onCreateLeague={() => navigate('/register')}
              />
            </div>
          )}

          {/* User Menu */}
          {user && (
            <UserMenu
              user={{ name: userName, email: userEmail, initials }}
              currentLeagueName={currentMembership?.league?.name}
              isSuperAdmin={isSuperAdmin}
              onProfile={() => navigate('/profile')}
              onLeagueSettings={() => navigate('/settings')}
              onRules={() => navigate('/rules')}
              onCreateLeague={() => navigate('/register')}
              onMission={() => navigate('/about', { state: { from: location.pathname } })}
              onTerms={() => navigate('/terms', { state: { from: location.pathname } })}
              onAdmin={() => navigate('/admin')}
              onLogout={() => signOut()}
            />
          )}
        </div>

        {/* Desktop/Tablet Navigation */}
        <div className="hidden md:block border-t border-zinc-100 dark:border-zinc-800">
          <div className="lg:max-w-6xl lg:mx-auto">
            <MainNav
              items={navigationItems}
              onNavigate={handleNavigate}
              variant="top"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
        <div className="lg:max-w-6xl lg:mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <MainNav
          items={navigationItems}
          onNavigate={handleNavigate}
          variant="bottom"
        />
      </div>

      <WelcomeModal />
    </div>
  )
}
